/**
 * Light, dark, or whatever the phone says.
 *
 * STORED IN localStorage, DELIBERATELY. Everything else in this app lives in
 * IndexedDB, but that is asynchronous — reading it means the app paints in one
 * theme and then swaps, which on a dark-set phone is a white flash in the eyes
 * at six in the morning. localStorage is synchronous and can be read by an
 * inline script before the first paint.
 *
 * This does not bend CLAUDE.md §10: that rule bans KEY MATERIAL from
 * localStorage. A theme preference is not a secret, and treating it like one
 * would buy nothing and cost a flash on every launch.
 *
 * The same three lines run in a script tag in index.html. Keep them in step —
 * that copy is what prevents the flash; this one is what the Settings screen
 * talks to.
 */
export type ThemeChoice = "system" | "light" | "dark";

export const THEME_KEY = "tally-pocket:theme";

export const THEME_OPTIONS: Array<{ value: ThemeChoice; label: string; hint: string }> = [
  { value: "system", label: "System", hint: "follow the phone" },
  { value: "light", label: "Light", hint: "always light" },
  { value: "dark", label: "Dark", hint: "always dark" },
];

/** The canvas colour per mode, so the browser chrome matches the app. */
const CHROME = { light: "#f5f7fa", dark: "#0a0e17" } as const;

export function loadTheme(): ThemeChoice {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === "light" || saved === "dark" ? saved : "system";
  } catch {
    // Private browsing on some engines throws on access rather than returning
    // null. A theme is not worth an exception on startup.
    return "system";
  }
}

/**
 * Apply a choice to the document.
 *
 * `data-theme` drives the CSS tokens; `color-scheme` drives the parts of the
 * page the CSS does not own — form controls, scrollbars, and the default
 * canvas behind the app before any of our own paint lands.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;

  if (choice === "system") {
    root.removeAttribute("data-theme");
    root.style.colorScheme = "light dark";
  } else {
    root.setAttribute("data-theme", choice);
    root.style.colorScheme = choice;
  }

  const dark =
    choice === "dark" ||
    (choice === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);

  // The Android status bar and the PWA splash read this. Left unchanged, a
  // reader who picks Light keeps a near-black bar above a white app.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? CHROME.dark : CHROME.light);
}

export function saveTheme(choice: ThemeChoice): void {
  try {
    if (choice === "system") localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
  } catch {
    // Nothing to do: the choice still applies for this session.
  }
  applyTheme(choice);
}

/**
 * Keep "System" honest while the app is open.
 *
 * Phones switch themes on a schedule. Without this, someone on System at sunset
 * watches the rest of the OS go dark around a white app until they reload.
 */
export function watchSystemTheme(): () => void {
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  if (!media) return () => {};

  const onChange = () => {
    if (loadTheme() === "system") applyTheme("system");
  };

  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
